'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MotionLink } from '@/components/ui/Motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographyP, TypographySmall } from '@/components/ui/typography';
import { URLS } from '@/lib/consts';
import { cls } from '@/lib/utils';
import { CityServiceLinksGrid } from '@/components/ui/sections/CityServiceLinks';

const SECTION_ID = 'service-area';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

const DESCRIPTION =
  `Cornerstone Homes is based in Claremore, Oklahoma, and provides design, build, and renovation services throughout Rogers County and the greater Tulsa metro area. Our 20+ years of building experience in northeastern Oklahoma means we understand the local terrain, soil conditions, building codes, and climate considerations that affect every project in this region.`;

const HIGHLIGHTS = [
  'Claremore, Owasso, Collinsville, and Catoosa',
  'Rogers County, Mayes County, and Wagoner County',
  'Broken Arrow, Jenks, Bixby, and greater Tulsa metro',
  'Inola, Pryor, Verdigris, and surrounding rural communities',
  'Grand Lake and Oologah Lake areas',
];

const TOWNS = [
  'Claremore',
  'Owasso',
  'Broken Arrow',
  'Tulsa',
  'Catoosa',
  'Collinsville',
  'Inola',
  'Verdigris',
  'Pryor',
  'Oologah',
];

interface ServiceAreaSectionProps {
  category: 'design-and-build' | 'design-and-renovate';
}

function ServiceAreaSection({ category }: ServiceAreaSectionProps) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="SERVICE AREAS"
          title="Proudly Serving Claremore & Northeastern Oklahoma"
          description="From Rogers County acreage to Tulsa metro communities, we build where you want to live."
        />

        <div className={cls`grid grid-cols-1 md:grid-cols-2 gap-8 w-full`}>
          <motion.div
            className={cls`flex flex-col gap-6`}
            {...itemMotionProps}
          >
            <TypographyP className="text-foreground leading-relaxed">
              {DESCRIPTION}
            </TypographyP>

            <div className="flex flex-col gap-2">
              <TypographyH4 className="text-foreground">
                Areas We Serve
              </TypographyH4>
              <ul className="flex flex-col gap-2">
                {HIGHLIGHTS.map((highlight) => (
                  <li key={highlight} className="flex items-start gap-2">
                    <CheckCircle className="text-primary size-5 mt-0.5 shrink-0" />
                    <TypographySmall className="text-muted-foreground">
                      {highlight}
                    </TypographySmall>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-2">
              <TypographyH4 className="text-foreground">
                Primary Service Towns
              </TypographyH4>
              <div className="flex flex-wrap gap-2">
                {TOWNS.map((town) => (
                  <span
                    key={town}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background border border-border text-sm text-muted-foreground"
                  >
                    <MapPin className="size-3.5 text-primary" />
                    {town}
                  </span>
                ))}
              </div>
            </div>

            <Button asChild className="w-fit">
              <MotionLink href={URLS.ABOUT.OUR_SERVICE_AREAS}>
                <MapPin className="mr-1" />
                View All Service Areas
              </MotionLink>
            </Button>
          </motion.div>

          <motion.div
            className={cls`w-full min-h-[400px] rounded-lg overflow-hidden`}
            {...itemMotionProps}
          >
            <iframe
              title="Cornerstone Homes service area - Claremore, Oklahoma"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51444.95865498607!2d-95.64!3d36.31!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87b6ed364f1e2d03%3A0x4e1e7b2d58558bce!2sClaremore%2C%20OK!5e0!3m2!1sen!2sus!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </motion.div>
      <CityServiceLinksGrid
        mode="by-city"
        category={category}
        title="Build in Your City"
        description={`Our ${category === 'design-and-build' ? 'design and build' : 'design and renovate'} services are available across 8 communities in northeastern Oklahoma.`}
      />
    </SectionBase>
  );
}

export default ServiceAreaSection;
